import { procedureSchema } from "@assetwatch/procedure-contract";
import {
  queryOptions,
  replaceEqualDeep,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { z } from "zod";
import { workApiFetch } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import type { WorkOrderId } from "../lib/workIds";

const responseValueSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("TEXT"), value: z.string() }),
  z.object({ type: z.literal("CHECKBOX"), value: z.boolean() }),
  z.object({ type: z.literal("NUMERIC"), value: z.number() }),
  z.object({ type: z.literal("SINGLE_CHOICE"), value: z.string() }),
  z.object({ type: z.literal("PHOTO"), fileIds: z.array(z.string()) }),
]);

// Display metadata for a saved photo; `fileId` matches an entry in the
// response's `value.fileIds`. The download URL is short-lived.
const responsePhotoSchema = z.object({
  fileId: z.string(),
  fileName: z.string(),
  contentType: z.string(),
  downloadUrl: z.string().url(),
});

const responseSchema = z.object({
  procedureId: z.string(),
  version: z.number(),
  itemId: z.string(),
  value: responseValueSchema,
  updatedAt: z.string(),
  updatedBy: z.string(),
  // Per-item concurrency token: a save of this item quotes it as
  // `expectedItemVersion`. Missing means the item has no saved response (0).
  revision: z.number().optional(),
  updatedByName: z.string().nullable().optional(),
  photos: z.array(responsePhotoSchema).optional(),
});

// What another user saved to an item, returned with a 409 so the page can keep
// the typed value and let the user choose. `version` is 0 and `value` null when
// the item currently has no answer.
const itemCurrentSchema = z.object({
  itemId: z.string(),
  value: responseValueSchema.nullable(),
  version: z.number(),
  updatedAt: z.string().nullable(),
  updatedBy: z.string().nullable(),
  updatedByName: z.string().nullable(),
});

const itemConflictSchema = z.object({
  error: z.string(),
  code: z.literal("ITEM_CONFLICT"),
  current: itemCurrentSchema,
});

const executionSchema = z.object({
  executionId: z.string(),
  procedureId: z.string(),
  version: z.number(),
  status: z.enum(["NOT_STARTED", "IN_PROGRESS", "COMPLETED"]),
  revision: z.number(),
  procedure: procedureSchema,
  responses: z.array(responseSchema),
  lastSavedAt: z.string().nullable(),
});

export type ProcedureExecution = z.infer<typeof executionSchema>;
export type ProcedureResponseValue = z.infer<typeof responseValueSchema>;
export type ProcedureResponsePhoto = z.infer<typeof responsePhotoSchema>;

export type ProcedureItemCurrent = z.infer<typeof itemCurrentSchema>;

/** One item's response; `value` null clears a text, number or choice answer. */
export type SaveProcedureItemInput = {
  value: ProcedureResponseValue | null;
  expectedItemVersion: number;
  idempotencyKey: string;
};

export type ProcedureExecutionScope = {
  customerId: string;
  facilityId: string;
  workOrderId: WorkOrderId;
};

/** A Procedure execution request the API rejected. */
export class ProcedureExecutionRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ProcedureExecutionRequestError";
  }
}

/**
 * Another user saved a different response to the same item first. Carries what
 * is stored now; the caller's own value was not saved and is still theirs.
 */
export class ProcedureItemConflictError extends ProcedureExecutionRequestError {
  constructor(
    message: string,
    readonly current: ProcedureItemCurrent,
  ) {
    super(message, 409);
    this.name = "ProcedureItemConflictError";
  }
}

// Signed photo URLs last 15 minutes, so saved progress is refetched before
// thumbnails can expire.
const procedureExecutionStaleTimeMs = 10 * 60 * 1000;

function executionPath({
  customerId,
  facilityId,
  workOrderId,
}: ProcedureExecutionScope) {
  return `/v1/customers/${encodeURIComponent(customerId)}/facilities/${encodeURIComponent(facilityId)}/work-orders/${encodeURIComponent(workOrderId)}/procedure/execution`;
}

async function parseExecutionResponse(response: Response) {
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const conflict = itemConflictSchema.safeParse(payload);
    if (response.status === 409 && conflict.success)
      throw new ProcedureItemConflictError(
        conflict.data.error,
        conflict.data.current,
      );
    const message =
      typeof payload === "object" &&
      payload !== null &&
      "error" in payload &&
      typeof payload.error === "string"
        ? payload.error
        : `Request failed (${response.status})`;
    throw new ProcedureExecutionRequestError(message, response.status);
  }
  return executionSchema.parse(payload);
}

/** Prefix shared by every cached Procedure execution; invalidate it after a Work Order status change. */
export const procedureExecutionQueryRootKey = [
  "work-order-procedure-execution",
] as const;

export function procedureExecutionQueryKey(
  customerId: string | undefined,
  facilityId: string | undefined,
  workOrderId: WorkOrderId,
) {
  return [
    ...procedureExecutionQueryRootKey,
    customerId,
    facilityId,
    workOrderId,
  ] as const;
}

export async function fetchProcedureExecution(scope: ProcedureExecutionScope) {
  return parseExecutionResponse(await workApiFetch(executionPath(scope)));
}

/**
 * Saves one item. Throws ProcedureItemConflictError when another user changed
 * the same item since `expectedItemVersion` and the value differs.
 */
export async function saveProcedureItem(
  scope: ProcedureExecutionScope,
  itemId: string,
  input: SaveProcedureItemInput,
) {
  return parseExecutionResponse(
    await workApiFetch(
      `${executionPath(scope)}/items/${encodeURIComponent(itemId)}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      },
    ),
  );
}

/** The per-item concurrency token the next save of this item must quote. */
export function procedureItemRevision(
  execution: ProcedureExecution | undefined,
  itemId: string,
) {
  return (
    execution?.responses.find((response) => response.itemId === itemId)
      ?.revision ?? 0
  );
}

/**
 * The newer of the cached execution and a save reply or refetch. Saves of
 * different items run concurrently and their replies can arrive out of order.
 * The cache only ever holds whole server snapshots (a conflict refetches rather
 * than patching one item in), and `revision` only increases, so comparing it is
 * enough: an older snapshot never replaces a newer one.
 */
export function newerProcedureExecution(
  cached: ProcedureExecution | undefined,
  reply: ProcedureExecution,
): ProcedureExecution {
  return cached && cached.revision > reply.revision ? cached : reply;
}

function keepNewerSnapshot(cached: unknown, fetched: unknown) {
  const previous = cached as ProcedureExecution | undefined;
  const newer = newerProcedureExecution(
    previous,
    fetched as ProcedureExecution,
  );
  return newer === previous ? previous : replaceEqualDeep(previous, newer);
}

export function procedureExecutionQueryOptions(input: {
  customerId: string | undefined;
  facilityId: string | undefined;
  workOrderId: WorkOrderId;
  enabled?: boolean;
}) {
  const { customerId, facilityId, workOrderId, enabled = true } = input;
  return queryOptions({
    queryKey: procedureExecutionQueryKey(customerId, facilityId, workOrderId),
    queryFn: () => {
      if (!customerId || !facilityId) {
        throw new Error("A customer and facility must be selected.");
      }
      return fetchProcedureExecution({ customerId, facilityId, workOrderId });
    },
    enabled: enabled && Boolean(customerId && facilityId && workOrderId),
    staleTime: procedureExecutionStaleTimeMs,
    // A refetch that started before a save committed must not land over it.
    structuralSharing: keepNewerSnapshot,
    meta: { errorMessage: "Failed to load saved Procedure progress." },
  });
}

export function useProcedureExecutionQuery(
  workOrderId: WorkOrderId,
  enabled = true,
) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  return useQuery(
    procedureExecutionQueryOptions({
      customerId: selectedCustomer?.id,
      facilityId: selectedFacility?.id,
      workOrderId,
      enabled,
    }),
  );
}

/**
 * A reader of the cached execution as it is right now. Unlike the query data of
 * a render, it sees a save result the moment it is stored, so a save queued
 * behind another quotes the token the first one returned.
 */
export function useCachedProcedureExecution(workOrderId: WorkOrderId) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();
  return () =>
    queryClient.getQueryData<ProcedureExecution>(
      procedureExecutionQueryKey(
        selectedCustomer?.id,
        selectedFacility?.id,
        workOrderId,
      ),
    );
}
